@if (isMenuItem) {
  <button mat-menu-item (click)="openLockDialog(lockDialog, $event)">
    <mat-icon class="lock-icon" [ngClass]="{
      'active-lock': object.lockerName,
      'comment-only': !object.lockerName && object.lockComment,
      'idle-lock': !object.lockerName && !object.lockComment
    }">{{ object.lockerName ? 'lock' : 'lock_open' }}</mat-icon>
    <span>{{ 'lock_title' | translate }}</span>
  </button>
} @else {
  <button mat-icon-button (click)="openLockDialog(lockDialog, $event)"
    matTooltipClass="whitespace-pre-line"
    [matTooltip]="getTooltipText()"
    [ngClass]="{
      'is-locked': object.lockerName,
      'has-comment-only': !object.lockerName && object.lockComment
    }"
    class="lock-trigger-btn">
    
    <div class="icon-container">
      @if (object.lockerName) {
        <!-- Locked State -->
        <mat-icon class="lock-icon active-lock">lock</mat-icon>
        <span class="status-indicator-dot locked-pulse"></span>
        @if (object.lockComment) {
          <span class="comment-badge"><mat-icon>notes</mat-icon></span>
        }
      } @else {
        <!-- Unlocked State -->
        @if (object.lockComment) {
          <!-- Has comment but not locked -->
          <mat-icon class="lock-icon comment-only">lock_open</mat-icon>
          <span class="status-indicator-dot comment-dot"></span>
        } @else {
          <!-- Idle State (completely unlocked and no comment) -->
          <mat-icon class="lock-icon idle-lock">lock_open</mat-icon>
        }
      }
    </div>
  </button>
}

<ng-template #lockDialog>
  <div class="lock-dialog-container p-6 min-w-[320px] max-w-[360px] flex flex-col gap-4">
    
    <!-- Menu Header -->
    <div class="menu-header pb-4 border-b border-solid border-slate-100 dark:border-slate-800/80 flex items-center justify-between gap-4">
      <div class="flex items-center gap-2.5 min-w-0">
        <div class="header-icon-badge shrink-0" [ngClass]="object.lockerName ? 'bg-amber-500/10 text-amber-500' : 'bg-emerald-500/10 text-emerald-500'">
          <mat-icon class="text-xl">{{ object.lockerName ? 'lock' : 'lock_open' }}</mat-icon>
        </div>
        <div class="min-w-0">
          <h4 class="m-0 tracking-tight">
            {{ 'lock_title' | translate }}
          </h4>
          <p class="m-0 text-xs text-slate-600 dark:text-slate-400 font-medium truncate">
            @if (object.lockerName) {
              {{ 'ADMIN.MANAGER' | translate }} : {{ object.lockerName }}
            } @else {
              {{ 'ADMIN.NONE' | translate }}
            }
          </p>
        </div>
      </div>
      
      <!-- Status Badge -->
      <span class="status-pill text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1 shrink-0"
        [ngClass]="object.lockerName ? 'bg-amber-500/10 text-amber-600 dark:text-amber-400' : 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'">
        <span class="w-1.5 h-1.5 rounded-full" [ngClass]="object.lockerName ? 'bg-amber-500 animate-pulse' : 'bg-emerald-500'"></span>
        {{ (object.lockerName ? 'locked' : 'unlocked') | translate }}
      </span>
    </div>

    @if (orderForm) {
      <form [formGroup]="orderForm" class="flex flex-col gap-4" (submit)="updateOrder(orderForm.value)">
        
        <!-- Quick Info Block -->
        <div class="info-block p-3 rounded-xl text-xs">
          <div class="flex flex-col gap-1.5">
            <div class="flex justify-between items-center">
              <span class="font-medium">{{ 'ADMIN.MANAGER' | translate }}</span>
              <span class="font-semibold">
                {{ orderForm.controls.lockerName.value || ('ADMIN.NONE' | translate) }}
              </span>
            </div>
            <div class="flex justify-between items-center">
              <span class="font-medium">{{ 'lastUpdate' | translate }}</span>
              <span class="font-semibold">
                {{ orderForm.controls.lockDate.value | date:'short' : '' : translate.currentLang }}
              </span>
            </div>
          </div>
        </div>

        <!-- Action Toggles -->
        <div class="action-toggle-row">
          @if (orderForm.controls.lockedBy.value !== 'none') {
            <button type="button" mat-flat-button class="unlock-btn w-full" (click)="unlock()" [disabled]="progress">
              <mat-icon>lock_open</mat-icon>
              {{ 'unlock' | translate }}
            </button>
          } @else {
            <button type="button" mat-flat-button color="primary" class="lock-btn w-full" (click)="lock()" [disabled]="orderForm.invalid || progress">
              <mat-icon>lock</mat-icon>
              {{ 'lock' | translate }}
            </button>
          }
        </div>

        <!-- Divider line -->
        <div class="border-t border-dashed border-slate-100 dark:border-slate-800/80 my-1"></div>

        <!-- Comments Area -->
        <div class="flex flex-col gap-1">
          <label class="text-xs font-semibold text-slate-600 dark:text-slate-400 ml-1 mb-1 flex items-center gap-1.5">
            <mat-icon class="text-sm w-4 h-4">notes</mat-icon>
            {{ 'BAT.COMMENTS' | translate }}
          </label>
          
          <div class="textarea-wrapper relative">
            <textarea 
              formControlName="lockComment" 
              rows="5" 
              placeholder="Écrire un commentaire..."
              class="w-full p-3 rounded-xl border border-solid border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 text-slate-800 dark:text-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all resize-none placeholder:text-slate-300 dark:placeholder:text-slate-700"
              data-lpignore="true" 
              data-form-type="other">
            </textarea>
          </div>
        </div>

        <!-- Footer Actions (Cancel & Save) -->
        <div class="flex flex-col gap-2.5 mt-2">
          <button type="submit" mat-flat-button color="primary" class="save-btn w-full text-sm font-semibold rounded-xl flex items-center justify-center gap-2"
            [disabled]="orderForm.invalid || progress">
            @if (!progress) {
              <mat-icon>save</mat-icon>
              {{ 'COMMON.SAVE' | translate }}
            } @else {
              <mat-spinner diameter="18" class="button-spinner"></mat-spinner>
              {{ 'COMMON.SAVE' | translate }}...
            }
          </button>
          <button type="button" mat-stroked-button class="cancel-btn w-full text-sm font-semibold rounded-xl flex items-center justify-center gap-2"
            (click)="closeDialog()" [disabled]="progress">
            <mat-icon>close</mat-icon>
            {{ 'COMMON.CANCEL' | translate }}
          </button>
        </div>

      </form>
    }
  </div>
</ng-template>
